import { Link, Stack, router, useFocusEffect, useLocalSearchParams, type Href } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';

import { EventAttendees } from '@/components/events/event-attendees';
import { EventCheckInPanel } from '@/components/events/event-check-in-panel';
import { EventDetails } from '@/components/events/event-details';
import { EventReminderControls } from '@/components/events/event-reminder-controls';
import { EventRegistrationButton } from '@/components/events/event-registration-button';
import { CalendarActionBadges } from '@/components/integrations/calendar-action-badges';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { fetchEventById, type Event } from '@/lib/events';
import { fetchTenantById, type Tenant } from '@/lib/tenant';
import { isEventEditable } from '@sixam/shared/event-editing-core';
import { useCurrentTime } from '@/lib/dev-clock';

type EventFooterProps = Readonly<{
  activeTenant: Tenant | null;
  event: Event;
  isGoing: boolean;
  isMember: boolean;
  userId: string | null;
}>;

function EventEditLink({ href }: Readonly<{ href: Href }>) {
  return (
    <Link href={href} asChild>
      <Button className="h-11 w-full" size="sm" variant="secondary">
        <Text>Edit run</Text>
      </Button>
    </Link>
  );
}

function EventGoingTools({
  event,
  isMember,
  tenant,
  userId,
}: Readonly<{ event: Event; isMember: boolean; tenant: Tenant; userId: string | null }>) {
  return (
    <>
      <CalendarActionBadges
        event={event}
        returnPath={`/app/events/${event.id}`}
        timeZone={tenant.timezone}
      />
      {isMember ? (
        <EventReminderControls
          event={event}
          tenantSlug={tenant.slug}
          timeZone={tenant.timezone}
          userId={userId}
        />
      ) : null}
    </>
  );
}

function EventDetailFooter({ activeTenant, event, isGoing, isMember, userId }: EventFooterProps) {
  // Bumped after a check-in so the attendees roster refetches its checked-in list.
  // Check-in happens on the full-screen station, so the roster is refetched
  // when this screen comes back into focus rather than after an inline scan.
  const [checkInVersion, setCheckInVersion] = React.useState(0);
  useFocusEffect(
    React.useCallback(() => {
      setCheckInVersion((version) => version + 1);
    }, [])
  );

  return (
    <>
      {activeTenant && isGoing ? (
        <EventGoingTools event={event} isMember={isMember} tenant={activeTenant} userId={userId} />
      ) : null}
      {activeTenant && isMember ? (
        <EventAttendees eventId={event.id} refreshKey={checkInVersion} />
      ) : null}
      {activeTenant ? <EventCheckInPanel eventId={event.id} userId={userId} /> : null}
    </>
  );
}

export default function AppEventDetailScreen() {
  const now = useCurrentTime();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { profile, session } = useSession();
  const { memberships, tenants } = useTenant();

  const [event, setEvent] = React.useState<Event | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isGoing, setIsGoing] = React.useState(false);
  const [resolvedTenant, setResolvedTenant] = React.useState<Tenant | null>(null);

  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const data = await fetchEventById(id);
        if (active) {
          setEvent(data);
          setResolvedTenant(null);
          setError(data ? null : 'Event not found.');
        }
      } catch (loadError: unknown) {
        if (active) {
          setEvent(null);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load event');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [id]);

  const handleGoingChange = React.useCallback((nextIsGoing: boolean) => {
    setIsGoing(nextIsGoing);
  }, []);

  const eventTenant = React.useMemo(
    () => tenants.find((tenant) => tenant.id === event?.tenant_id) ?? resolvedTenant,
    [event?.tenant_id, resolvedTenant, tenants]
  );
  React.useEffect(() => {
    if (!event?.tenant_id || tenants.some((tenant) => tenant.id === event.tenant_id)) {
      return;
    }
    let active = true;
    void fetchTenantById(event.tenant_id)
      .then((tenant) => {
        if (active) setResolvedTenant(tenant);
      })
      .catch(() => {
        if (active) setResolvedTenant(null);
      });
    return () => {
      active = false;
    };
  }, [event, tenants]);
  const membershipRole = React.useMemo(
    () => memberships.find((membership) => membership.tenant_id === event?.tenant_id)?.role ?? null,
    [event?.tenant_id, memberships]
  );
  const isMember = Boolean(membershipRole);
  const isClubAdmin = ['admin', 'owner'].includes(membershipRole ?? '');
  const isGlobalAdmin = profile?.role === 'admin';
  const isPersonalEvent = event?.tenant_id === null;
  const isCreator = Boolean(
    session?.user.id &&
    event?.created_by &&
    event.created_by.localeCompare(session.user.id) === 0 &&
    (isMember || isPersonalEvent)
  );
  const canEditEvent = isGlobalAdmin || isClubAdmin || isCreator;
  let editHref: Href | null = null;
  if (event && canEditEvent && isEventEditable(event, now)) {
    editHref =
      isGlobalAdmin || isClubAdmin
        ? { pathname: '/admin/events/[id]', params: { id: event.id } }
        : { pathname: '/app/events/edit/[id]', params: { id: event.id } };
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: true, title: 'Event' }} />
      <Stack.Toolbar placement="left">
        <Stack.Toolbar.Button
          accessibilityLabel="Back to Home"
          icon={process.env.EXPO_OS === 'ios' ? 'chevron.left' : undefined}
          onPress={() => router.replace('/app/home')}
        >
          {process.env.EXPO_OS === 'ios' ? undefined : 'Back'}
        </Stack.Toolbar.Button>
      </Stack.Toolbar>

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
      >
        {isLoading ? <ActivityIndicator /> : null}

        {error ? (
          <Text className="text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {event ? (
          <EventDetails
            event={event}
            managementAction={editHref ? <EventEditLink href={editHref} /> : null}
            timeZone={eventTenant?.timezone}
            primaryAction={
              eventTenant ? (
                <EventRegistrationButton
                  key={event.id}
                  event={event}
                  onGoingChange={handleGoingChange}
                  returnPath={`/app/events/${event.id}`}
                  tenantName={eventTenant.name}
                  tenantSlug={eventTenant.slug}
                  timeZone={eventTenant.timezone}
                  userId={session?.user.id ?? null}
                />
              ) : null
            }
            footer={
              <EventDetailFooter
                activeTenant={eventTenant}
                event={event}
                isGoing={isGoing}
                isMember={isMember}
                userId={session?.user.id ?? null}
              />
            }
          />
        ) : null}
      </ScrollView>
    </>
  );
}
